<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Dayjs } from 'dayjs'
import { Color } from '@previo/codebooks'
import { $t } from '@previo/core'
import PSelectInputField from '../../../fields/PSelectInputField.vue'
import PTextFormBody from '../../../typography/form/PTextFormBody.vue'
import { PDatePickerFastChoice, type PDatePickerFastChoiceItem } from '../../../date-picker/types'
import { dateFastChoicesList } from '../../../date-picker/constants'
import { applyDefaultTime, resolveDateFastChoice } from '../../../date-picker/utils'
import { usePrevioDS } from '../../../../composables/helpers'
import { useDateTimePicker } from '../../composables/DateTimePickerState'

type Props = {
  fastChoices?: PDatePickerFastChoice[]
  defaultTime?: string
}

type Emits = {
  (e: 'select'): void
}

const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const model = defineModel<Dayjs | null>()

const ds = usePrevioDS()
const navigation = useDateTimePicker()

const selectedFastChoice = ref<PDatePickerFastChoice>()
const visibleFastChoice = computed<PDatePickerFastChoiceItem[]>(() => {
  const visible: PDatePickerFastChoiceItem[] = []

  props.fastChoices?.map((choiceType) => {
    const choice = dateFastChoicesList.find((choice) => choice.type === choiceType)

    if (choice) {
      visible.push(choice)
    }
  })

  return visible
})

const hasFastChoice = computed<boolean>(() => {
  return visibleFastChoice.value.length > 0
})

watch(selectedFastChoice, (value) => {
  const date = resolveDateFastChoice(value).tz()
  const dateWithTime = model.value
    ? date.withTimeFrom(model.value)
    : applyDefaultTime(date, props.defaultTime)

  model.value = dateWithTime
  navigation.setDate(dateWithTime)
  emits('select')
})
</script>

<template>
  <p-select-input-field
    v-if="hasFastChoice && ds.screen().isMobile"
    v-model="selectedFastChoice"
    :items="visibleFastChoice"
    :placeholder="$t('Choice fast filter')"
    item-value="type"
    item-text="name"
    cy="fast-choice-select"
  >
    <template #item="{ item }: { item: PDatePickerFastChoiceItem }">
      <p-text-form-body
        :text="$t(item.name)"
        :color="selectedFastChoice === item.type ? Color.PRIMARY : undefined"
      />
    </template>
  </p-select-input-field>
</template>
